import { useState } from 'react';

const PAYSTACK_URL = 'https://paystack.shop/pay/v9-4hu3n50';

const sizes = [
  {
    size: '50g',
    price: 'GHS 20',
    priceNum: 20,
    servings: '~16 servings',
    perServing: 'GHS 1.25 / serving',
    tag: '',
    desc: 'Great starter pack — try it for 2 weeks',
    icon: 'ri-flask-line',
  },
  {
    size: '100g',
    price: 'GHS 35',
    priceNum: 35,
    servings: '~33 servings',
    perServing: 'GHS 1.06 / serving',
    tag: '',
    desc: 'Perfect for a full month of daily use',
    icon: 'ri-cup-line',
  },
  {
    size: '250g',
    price: 'GHS 75',
    priceNum: 75,
    servings: '~83 servings',
    perServing: 'GHS 0.90 / serving',
    tag: 'Best Value',
    desc: 'Most popular — 2–3 months supply',
    icon: 'ri-trophy-line',
  },
  {
    size: '1kg',
    price: 'GHS 250',
    priceNum: 250,
    servings: '~333 servings',
    perServing: 'GHS 0.75 / serving',
    tag: 'Bulk Saver',
    desc: 'Ideal for families or wholesale resale',
    icon: 'ri-store-line',
  },
];

export default function MoringaSizes() {
  const [selected, setSelected] = useState(2); // default to 250g

  return (
    <section className="py-20 bg-white">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-12">
          <span className="inline-flex items-center bg-green-50 text-green-700 text-xs font-semibold px-4 py-1.5 rounded-full border border-green-200 uppercase tracking-widest mb-4">
            <i className="ri-price-tag-3-line mr-2"></i>Sizes &amp; Pricing
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Choose Your Pack Size
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto">
            All sizes are the same pure product. Bigger packs give better value per serving.
          </p>
        </div>

        {/* Size cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
          {sizes.map((s, i) => {
            const isSelected = selected === i;
            return (
              <button
                key={i}
                onClick={() => setSelected(i)}
                className={`relative text-left rounded-2xl border-2 p-6 transition-all cursor-pointer w-full ${
                  isSelected
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 bg-white hover:border-green-300'
                }`}
              >
                {/* Tag badge */}
                {s.tag && (
                  <span className={`absolute top-4 right-4 text-xs font-bold px-2.5 py-1 rounded-full ${
                    s.tag === 'Best Value' ? 'bg-green-600 text-white' : 'bg-amber-500 text-white'
                  }`}>
                    {s.tag}
                  </span>
                )}

                {/* Icon */}
                <span className={`w-10 h-10 flex items-center justify-center rounded-xl mb-4 ${
                  isSelected ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  <i className={`${s.icon} text-xl`}></i>
                </span>

                {/* Size + price */}
                <div className="text-2xl font-extrabold text-gray-900 mb-1">{s.size}</div>
                <div className={`text-xl font-bold mb-1 ${isSelected ? 'text-green-600' : 'text-gray-700'}`}>{s.price}</div>
                <div className="text-xs text-gray-400 mb-3">{s.perServing}</div>

                {/* Servings */}
                <div className="text-xs font-semibold text-gray-500 bg-gray-100 rounded-lg px-3 py-1.5 inline-block mb-4">
                  {s.servings}
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-snug">{s.desc}</p>

                {/* Selected indicator */}
                {isSelected && (
                  <div className="absolute bottom-4 right-4 w-6 h-6 flex items-center justify-center bg-green-600 rounded-full">
                    <i className="ri-check-line text-white text-sm"></i>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected pack summary + CTA */}
        <div className="bg-green-600 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-white">
            <p className="text-sm font-semibold text-green-200 uppercase tracking-wider mb-1">Selected</p>
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-extrabold">{sizes[selected].size}</span>
              <span className="text-2xl font-bold text-green-200">— {sizes[selected].price}</span>
            </div>
            <p className="text-green-200 text-sm mt-1">{sizes[selected].servings} · {sizes[selected].perServing}</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
            <a
              href={PAYSTACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center bg-white text-green-700 hover:bg-green-50 font-bold px-8 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-base"
            >
              <i className="ri-shopping-bag-line mr-2 text-lg"></i>
              Order {sizes[selected].size} — {sizes[selected].price}
            </a>
            <a
              href="/contact"
              className="inline-flex items-center justify-center border-2 border-white/50 text-white hover:bg-white/10 font-semibold px-6 py-4 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              Bulk / Wholesale
            </a>
          </div>
        </div>

        {/* Trust notes */}
        <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-gray-500">
          {[
            { icon: 'ri-secure-payment-line', text: 'Secure payment via Paystack' },
            { icon: 'ri-truck-line', text: 'Delivery across Ghana' },
            { icon: 'ri-leaf-line', text: 'Freshly processed, airtight sealed' },
            { icon: 'ri-phone-line', text: 'Call us: +233 23 337 1378' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center text-green-600">
                <i className={`${item.icon} text-base`}></i>
              </span>
              {item.text}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
